import {
    Box,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    listItemButtonClasses,
    ListItemContent,
    Sheet, Stack,
    Typography
} from "@mui/joy";
import {Category, Close, Construction, Group, Home, Label, Map, Translate} from "@mui/icons-material";
import React, {useContext, useEffect} from "react";
import {Link} from "react-router-dom";
import {useLocation} from "react-router-dom";
import {AuthContext} from "./AuthProvider";
import {FeedbackContext} from "./FeedbackContext";
import {projectTitle} from "./Utils";
import firebase from "firebase/compat/app";

function Toggler({
                     defaultExpanded = false,
                     renderToggle,
                     children,
                 }: {
    defaultExpanded?: boolean;
    children: React.ReactNode;
    renderToggle: (params: {
        open: boolean;
        setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    }) => React.ReactNode;
}) {
    const [open, setOpen] = React.useState(defaultExpanded);
    return (
        <React.Fragment>
            {renderToggle({ open, setOpen })}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateRows: open ? '1fr' : '0fr',
                    transition: '0.2s ease',
                    '& > *': {
                        overflow: 'hidden',
                    },
                }}
            >
                {children}
            </Box>
        </React.Fragment>
    );
}

export function Sidebar() {

    const {setError} = useContext(FeedbackContext)
    const {userCompany, user} = useContext(AuthContext)

    const {currentUser} = useContext(AuthContext)
    const location = useLocation();
    const pathname = location.pathname

    useEffect(() => {
        closeSidebar()
    }, [location]);


    return (
        <>
            <Box
                className="Sidebar-overlay"
                sx={{
                    position: 'fixed',
                    zIndex: 9997,
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    opacity: 'var(--SideNavigation-slideIn)',
                    backgroundColor: 'var(--joy-palette-background-backdrop)',
                    transition: 'opacity 0.4s',
                    transform: {
                        xs: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1) + var(--SideNavigation-slideIn, 0) * var(--Sidebar-width, 0px)))',
                        lg: 'translateX(-100%)',
                    },
                    display: {
                        xs: "block",
                        md: "none"
                    }
                }}
                onClick={() => closeSidebar()}
            />
            <Sheet
                className="Sidebar"
                sx={{
                    position: { xs: 'fixed', md: 'fixed' },
                    transform: {
                        xs: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))',
                        md: 'none',
                    },
                    transition: 'transform 0.4s, width 0.4s',
                    zIndex: 100,
                    height: '100%',
                    minHeight: '100dvh',
                    width: {
                        xs: '100vw',
                        md: '20vw',
                        lg: '15vw',
                    },
                    top: 0,
                    p: 2,
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    borderRight: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Box sx={{ display: 'flex', gap: 1, justifyContent:"center", alignItems: 'center' }}>
                    <IconButton
                        onClick={() => toggleSidebar()}
                        variant="plain"
                        color="neutral"
                        size="sm"
                        sx={{position:"absolute", top:"1rem", left:"1rem", display: {xs:"block", md:"none"}}}
                    >
                        <Close />
                    </IconButton>
                    <Typography level="title-lg">{projectTitle}</Typography>
                </Box>
                <Divider />
                <Box
                    sx={{
                        minHeight: 0,
                        overflow: 'hidden auto',
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        [`& .${listItemButtonClasses.root}`]: {
                            gap: 1.5,
                        },
                    }}
                >
                    <List
                        size="sm"
                        sx={{
                            gap: 1,
                            '--List-nestedInsetStart': '30px',
                            '--ListItem-radius': (theme) => theme.vars.radius.sm,
                        }}
                    >
                        {
                            // add pages here
                        }
                        <ItemLinkMenu
                            title="Home"
                            icon={<Home />}
                            link="/"
                        />
                        <ItemLinkMenu
                            title="Categorie"
                            icon={<Category />}
                            link="/categorie"
                        />
                        <ItemLinkMenu
                            title="Servizi"
                            icon={<Label />}
                            link="/servizi"
                        />
                        <ItemLinkMenu
                            title="Mappa"
                            icon={<Map />}
                            link="/mappa"
                        />
                        <ItemLinkMenu
                            title="Utenti"
                            icon={<Group />}
                            link="/utenti"
                        />
                        <ItemLinkMenu
                            title="Traduzioni"
                            icon={<Translate />}
                            link="/traduzioni"
                        />
                        <ItemLinkMenu
                            title="Impostazioni"
                            icon={<Construction />}
                            link="/impostazioni"
                        />

                    </List>
                </Box>
                <Divider />
                <Stack direction="column" spacing={0}>
                    <Typography level="body-xs">{currentUser.email}</Typography>
                    <Typography level="body-xs"
                                onClick={async () => {
                                    if (window.confirm("Effettuare la disconnessione?")) {
                                        await firebase.auth().signOut()
                                    }
                                }}
                                className="cursorPointer"
                                sx={{textDecoration: "underline"}}
                    >
                        Disconnettiti
                    </Typography>
                </Stack>
            </Sheet>
        </>
    );

}

function ItemLinkMenu(props: {title: string, icon: React.ReactNode, link: string}) {
    const location = useLocation();
    const pathname = location.pathname
    let active: boolean = pathname.includes(props.link)
    return (
        <ListItem variant={active ? "soft" : "plain"}>
            <Link to={props.link} style={{width:"100%"}}>
                <ListItemButton>
                    {props.icon}
                    <ListItemContent>
                        <Typography level="title-sm">{props.title}</Typography>
                    </ListItemContent>
                </ListItemButton>
            </Link>
        </ListItem>
    )
}

export function openSidebar() {
    if (typeof window !== 'undefined') {
        document.body.style.overflow = 'hidden';
        document.documentElement.style.setProperty('--SideNavigation-slideIn', '1');
    }
}

export function closeSidebar() {
    if (typeof window !== 'undefined') {
        document.documentElement.style.removeProperty('--SideNavigation-slideIn');
        document.body.style.removeProperty('overflow');
    }
}

export function toggleSidebar() {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        const slideIn = window
            .getComputedStyle(document.documentElement)
            .getPropertyValue('--SideNavigation-slideIn');
        if (slideIn) {
            closeSidebar();
        } else {
            openSidebar();
        }
    }
}